import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { tr } from "./translations";
import { devWarn } from "@/lib/devlog";

const I18nContext = createContext(null);
const SUPPORTED = ["it", "en", "fr"];
const DEFAULT_LANG = "it";

/**
 * Country → language map for IP-based auto-detect.
 * Only currently-supported languages listed; other countries default to EN
 * (business-safe fallback rather than IT).
 */
const COUNTRY_LANG = {
    IT: "it", SM: "it", VA: "it",                       // Italy, San Marino, Vatican
    FR: "fr", BE: "fr", LU: "fr", MC: "fr", CH: "fr",   // French-speaking
    GB: "en", US: "en", IE: "en", CA: "en", AU: "en", NZ: "en",
};

function detectInitialLang() {
    if (typeof window === "undefined") return DEFAULT_LANG;
    // 1) URL query param (?lang=en, ?lang=fr) — highest priority for hreflang
    try {
        const params = new URLSearchParams(window.location.search);
        const q = params.get("lang");
        if (q && SUPPORTED.includes(q)) return q;
    } catch (e) { devWarn("[i18n] URL lang detection failed:", e); }
    // 2) explicit URL prefix (/en/..., /fr/...)
    if (window.location.pathname.startsWith("/en")) return "en";
    if (window.location.pathname.startsWith("/fr")) return "fr";
    // 3) localStorage (user's previous choice)
    try {
        const saved = window.localStorage.getItem("itly_lang");
        if (saved && SUPPORTED.includes(saved)) return saved;
    } catch (e) { devWarn("[i18n] localStorage unavailable:", e); }
    // 4) browser preference — checks primary language tag only
    const nav = (navigator.language || "it").toLowerCase();
    if (nav.startsWith("fr")) return "fr";
    if (nav.startsWith("en")) return "en";
    return DEFAULT_LANG;
}

/**
 * IP geolocation via ipapi.co (free, no API key, CORS-enabled).
 * Called only on first-ever visit (when localStorage is empty AND no URL
 * query has been used) — silently swallows any error to never block UI.
 */
async function detectFromIp() {
    try {
        const resp = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!resp.ok) return null;
        const data = await resp.json();
        const country = (data?.country_code || data?.country || "").toUpperCase();
        return COUNTRY_LANG[country] || null;
    } catch {
        return null;
    }
}

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(detectInitialLang);

    // First-visit IP auto-detect. Only runs if the user hasn't already made
    // an explicit choice (no localStorage, no ?lang= query in this session).
    useEffect(() => {
        let alive = true;
        const alreadyChosen = (() => {
            try {
                if (window.localStorage.getItem("itly_lang")) return true;
                if (window.localStorage.getItem("itly_lang_ip_checked")) return true;
                const q = new URLSearchParams(window.location.search).get("lang");
                if (q && SUPPORTED.includes(q)) return true;
                if (window.location.pathname.startsWith("/en")) return true;
                if (window.location.pathname.startsWith("/fr")) return true;
            } catch (e) { devWarn("[i18n] choice detection failed:", e); }
            return false;
        })();
        if (alreadyChosen) return undefined;

        detectFromIp().then((ipLang) => {
            if (!alive) return;
            try { window.localStorage.setItem("itly_lang_ip_checked", "1"); } catch (e) { devWarn("[i18n] cannot persist ip-check flag:", e); }
            if (ipLang && SUPPORTED.includes(ipLang) && ipLang !== lang) {
                setLangState(ipLang);
            }
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // mount-only by design: IP auto-detect must run once, before any user choice

    // Persist choice + set <html lang="...">. If the cookie banner is still open
    // (no consent yet) and the language changed, reload so Iubenda re-renders it
    // in the new language (its banner cannot switch language at runtime).
    const prevLangRef = useRef(null);
    useEffect(() => {
        try { window.localStorage.setItem("itly_lang", lang); } catch (e) { devWarn("[i18n] cannot persist language:", e); }
        if (typeof document !== "undefined") {
            document.documentElement.lang = lang;
            const changed = prevLangRef.current !== null && prevLangRef.current !== lang;
            if (changed && document.getElementById("iubenda-cs-banner")) {
                const url = new URL(window.location.href);
                if (url.searchParams.has("lang")) url.searchParams.set("lang", lang);
                window.location.replace(url.toString());
                return;
            }
        }
        prevLangRef.current = lang;
    }, [lang]);

    const setLang = useCallback((next) => {
        if (SUPPORTED.includes(next)) setLangState(next);
    }, []);

    const t = useCallback(
        (key, params = null) => tr(key, lang, params),
        [lang],
    );

    const value = useMemo(
        () => ({ lang, setLang, t, supported: SUPPORTED }),
        [lang, setLang, t],
    );
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
    return ctx;
}
